import React, { useMemo } from 'react';
import { Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, makeStyles } from '@material-ui/core';
import {
	ChevronLeft,
	ChevronRight,
	Close,
	ClosedCaption,
	Description,
	RadioButtonChecked,
	RadioButtonUnchecked,
	Speed,
	TuneOutlined,
	VolumeDown,
	Settings,
} from '@material-ui/icons';
import { generateUUID, getTranslateKey, joinClassNames } from '../../../util/util';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { isSafari } from 'react-device-detect';

export const useListCustomStyle = makeStyles(() => ({
	listItem: {
		minWidth: '190px',
		color: '#fff',
		cursor: 'pointer',
	},
	listItemMobile: {
		width: '150px',
		color: '#fff',
	},
	listItemFocus: {
		outline: '1px dash white',
	},
	listItemIcon: {
		fontSize: '1em',
	},
	primaryListText: {
		fontSize: '0.75em',
		lineHeight: 1.5,
		cursor: 'pointer',
		textTransform: 'none',
	},
	focusButton: {
		'&:focus': {
			color: '#ffffff',
		},
	},
    inputRoot: {
        fontSize: '16px',
    },
    input: {
        fontSize: '0.75em',
        lineHeight: 1.5,
        cursor: 'pointer',
    },
}));

export namespace MenuUtils {
	const getMenuIcon = (menuType: string, className: string) => {
		const processedClassName = joinClassNames(className, isSafari ? 'safari-fix' : '');
		switch (menuType) {
			case 'language':
				return <VolumeDown className={processedClassName} htmlColor="#fff" aria-hidden="true" />;
			case 'subtitle':
				return <ClosedCaption className={processedClassName} htmlColor="#fff" aria-hidden="true" />;
			case 'transcription':
				return <Description className={processedClassName} htmlColor="#fff" aria-hidden="true" />;
			case 'playbackSpeed':
				return <Speed className={processedClassName} htmlColor="#fff" aria-hidden="true" />;
			case 'quality':
				return <TuneOutlined className={processedClassName} htmlColor="#fff" aria-hidden="true" />;
			default:
				return <></>;
		}
	};
	interface MenuPanelProps {
		menuType: string;
		items: any[];
		isMobile?: boolean;
		showAllMenus?: boolean;
		hoveringItem?: any;
		selectedItem?: any;
		playerConfigs: any;
		selectedMenu?: string;
		container: HTMLElement | Document;
		isMediaLive?: boolean;
		onMenuItemClick: (_e: React.MouseEvent, _item: any, _type?: any) => void;
		onKeyDown: (_e: React.KeyboardEvent, _item: any, _type?: string) => void;
		onCloseMenu: () => void;
		onHoverItem: (_item: any) => void;
		onLeaveItem: () => void;
		onSelectMenu: (_menu: string) => void;
		onGoBack?: () => void;
		openSubtitleSettingMenu?: (_e: React.KeyboardEvent) => void;
	}

	export const MenuPanel = ({
		menuType,
		items,
		isMobile,
		hoveringItem,
		selectedItem,
		playerConfigs,
		showAllMenus,
		container,
		isMediaLive = false,
		onSelectMenu,
		onMenuItemClick,
		onKeyDown,
		onCloseMenu,
		onHoverItem,
		onLeaveItem,
		onGoBack,
		openSubtitleSettingMenu,
	}: MenuPanelProps) => {
		const { t } = useTranslation();

		const menuKey = useMemo(() => generateUUID(), []);
		const color = useMemo(() => playerConfigs?.playerStyling?.primaryColor ?? '#126cfc', [playerConfigs?.playerStyling?.primaryColor]);
		const opacityN = useMemo(() => Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100), [playerConfigs?.playerStyling?.primaryOpacity]);
		const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacityN / 100 };
		const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
		const classes = useListCustomStyle();
		const backBtnKey = `${menuType}-back`;
		const { titleLabel, defaultItemLabel, defaultItem, subHeaderId } = useMemo(() => {
			let titleLabel = '';
			let defaultItemLabel = '';
			let defaultItem = '';
			let subHeaderId = '';

			switch (menuType) {
				case 'language':
					titleLabel = t('AUDIO_LANGUAGE');
					defaultItemLabel = t('LABEL_DEFAULT');
					defaultItem = 'default';
					subHeaderId = 'language-sub-header';
					break;
				case 'subtitle':
					titleLabel = t('SUBTITLES');
					defaultItemLabel = t('LABEL_NONE');
					defaultItem = 'none';
					subHeaderId = 'language-sub-header';
					break;
				case 'transcription':
					titleLabel = t('TRANSCRIPTION');
					defaultItemLabel = t('LABEL_NONE');
					defaultItem = 'default';
					subHeaderId = 'language-sub-header';
					break;
				case 'playbackSpeed':
					titleLabel = t('PLAYBACK_SPEED');
					defaultItem = '1';
					subHeaderId = 'quality-sub-header';
					break;
				case 'quality':
					titleLabel = t('QUALITY');
					defaultItem = '1';
					subHeaderId = 'quality-sub-header';
					break;
				default:
					titleLabel = menuType;
					defaultItemLabel = t('LABEL_DEFAULT');
					defaultItem = 'default';
					break;
			}

			return {
				titleLabel,
				defaultItemLabel,
				defaultItem,
				subHeaderId,
			};
		}, [menuType, t]);

		const handleKeydownLanguageSetting = (event: any, type: any) => {
			if (event.key === 'Tab') {
				event.stopPropagation();
				return;
			}
			if (event.key === 'Enter' || event.key === ' ') {
				onSelectMenu(type);
			}
		};

		const handleKeyDownOnOptions = (event: any, item: any, menuType: any) => {
			const isLanguageType = menuType === 'subtitle' || menuType === 'language' || menuType === 'transcript';
			const isSettingType = menuType === 'playbackSpeed' || menuType === 'quality';

			if (event.key?.includes('Arrow')) {
				event.preventDefault();
				event.stopPropagation();

				const itemsToFocus = defaultItemLabel
					? [isLanguageType ? { language: { code: defaultItem } } : { code: defaultItem }, ...items]
					: items;

				const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;

				const currentItemIndex = itemsToFocus.findIndex((queryItem) => {
					if (isLanguageType) {
						return menuType === 'language'
							? item?.code === queryItem.language.code && item?.impaired === queryItem.language.impaired
							: item?.code === queryItem.language.code;
					}

					if (isSettingType) {
						return (item?.value ?? item?.label) === (queryItem.value ?? queryItem.label ?? queryItem.label);
					}
					return 0;
				});
				const nextItemIndex = (currentItemIndex + delta + itemsToFocus.length) % itemsToFocus.length;

				if (defaultItemLabel) {
					if (nextItemIndex === 0) {
						const itemsToFocus = (container.querySelector(`.${menuType}-default`) as HTMLElement) || (document.body.querySelector(`.${menuType}-default`) as HTMLElement);
						itemsToFocus && itemsToFocus.focus();
					} else {
						const itemsToFocus = (container.querySelector(`.${menuType}-item-${nextItemIndex - 1}`) as HTMLElement) || (document.body.querySelector(`.${menuType}-item-${nextItemIndex - 1}`) as HTMLElement);
						itemsToFocus && itemsToFocus.focus();
					}
				} else {
					const itemsToFocus = (container.querySelector(`.${menuType}-item-${nextItemIndex}`) as HTMLElement) || (document.body.querySelector(`.${menuType}-item-${nextItemIndex}`) as HTMLElement);
					itemsToFocus && itemsToFocus.focus();
				}
			}

			if (event.key === 'Enter' || event.key === ' ') {
				onKeyDown(event, item, menuType);
			}
		};

		const getItemPrimaryText = (item: any) => {
			switch (menuType) {
				case 'transcription':
				case 'language':
					return `${t((item?.language?.code ?? 'en').toUpperCase())} ${item?.language?.impaired ? '(' + t('AUDIO_DESCRIPTION') + ')' : ''}`;
				case 'subtitle':
					return `${t(getTranslateKey(item?.language?.code ?? 'en').toUpperCase())} ${item?.language?.impaired ? '[CC]' : ''}`;
				default:
					return t((item?.language?.code ?? 'en').toUpperCase());
			}
		};

		return (
			<>
				<ListItem
					tabIndex={showAllMenus ? 0 : undefined}
					className={classes.listItem}
					onClick={showAllMenus ? () => onGoBack?.() : undefined}
					onKeyDown={showAllMenus ? (e: any) => handleKeydownLanguageSetting(e, '') : undefined}
					style={hoveringItem?.code === backBtnKey ? hoverStyle : undefined}
					onMouseOver={showAllMenus ? () => onHoverItem({ code: backBtnKey }) : undefined}
					onMouseLeave={showAllMenus ? () => onLeaveItem() : undefined}
					onFocus={showAllMenus ? () => onHoverItem({ code: backBtnKey }) : undefined}
					onBlur={showAllMenus ? () => onLeaveItem() : undefined}
					aria-label={showAllMenus ? `${t('LABEL_GO_BACK_BTN')}` : undefined}
					component={'div'}
					role={showAllMenus ? 'button' : undefined}
				>
					<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
						{showAllMenus ? (
							<ChevronLeft className={joinClassNames(classes.listItemIcon, isSafari ? 'safari-fix' : '')} htmlColor="#fff" aria-hidden="true" />
						) : (
							getMenuIcon(menuType, classes.listItemIcon)
						)}
					</ListItemIcon>
					<ListItemText id={subHeaderId} classes={{ primary: classes.primaryListText }} primary={titleLabel} />
					{!showAllMenus && isMobile && (
						<IconButton
							aria-label={t('LABEL_CLOSE')}
							style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
							onClick={onCloseMenu}
							className={classes.focusButton}
						>
							<Close className={joinClassNames(classes.listItemIcon, isSafari ? 'safari-fix' : '')} htmlColor="#fff" />
						</IconButton>
					)}
				</ListItem>
				<Divider aria-hidden="true" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '1px' }} />

				<List
					className={joinClassNames('qplayer__language-panel', isMobile ? 'qplayer__setting-menu--mobile-scrollable' : '')}
					onKeyDown={(e: any) => {
						onKeyDown(e, selectedItem, menuType);
					}}
					role="menu"
					aria-label={titleLabel}
				>
					{defaultItemLabel && (
						<ListItem
							className={joinClassNames(classes.listItem, `${menuType}-default`)}
							onKeyDown={(e) => handleKeyDownOnOptions(e, { code: defaultItem }, menuType)}
							tabIndex={selectedItem?.code === defaultItem || selectedItem?.code === 'und' ? 0 : -1}
							key={`${menuType}-default-${menuKey}`}
							value={defaultItem}
							onClick={(e) => onMenuItemClick(e, { code: defaultItem }, menuType)}
							style={hoveringItem?.code === defaultItem || hoveringItem?.code === 'und' ? hoverStyle : undefined}
							onMouseOver={() => {
								if (hoveringItem?.code !== defaultItem) {
									onHoverItem({ code: defaultItem });
								}
							}}
							onMouseLeave={() => {
								if (hoveringItem?.code === defaultItem) {
									onLeaveItem();
								}
							}}
							onFocus={() => {
								if (hoveringItem?.code !== defaultItem) {
									onHoverItem({ code: defaultItem });
								}
							}}
							onBlur={() => {
								if (hoveringItem?.code === defaultItem) {
									onLeaveItem();
								}
							}}
							role="menuitemradio"
							aria-checked={selectedItem?.code === defaultItem || selectedItem?.code === 'und' ? 'true' : 'false'}
						>
							<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
								{selectedItem?.code === defaultItem || selectedItem?.code === 'und' ? (
									<RadioButtonChecked className={classes.listItemIcon} htmlColor="#fff" aria-hidden="true" />
								) : (
									<RadioButtonUnchecked className={classes.listItemIcon} htmlColor="#fff" aria-hidden="true" />
								)}
							</ListItemIcon>
							<ListItemText
								classes={{ primary: classes.primaryListText }}
								primary={defaultItemLabel}
								aria-label={menuType === 'subtitle' ? `${t('SUBTITLES')} ${t('LABEL_NONE')}` : undefined}
							/>
						</ListItem>
					)}

					{(menuType === 'playbackSpeed' || menuType === 'quality') &&
						items.map((item: any, index: number) => {
							if (!item) {
								return null;
							}

							const isSelected = selectedItem === (item.value ?? item.label ?? item.label);
							const isHovering = hoveringItem === (item.value ?? item.label);
							return (
								<ListItem
									className={joinClassNames(classes.listItem, `${menuType}-item-${index}`)}
									onKeyDown={(e) => handleKeyDownOnOptions(e, item, menuType)}
									tabIndex={isSelected ? 0 : -1}
									key={`${item.value ?? item.label}-${index}-${menuKey}`}
									value={item.value ?? item.label}
									onClick={(e) => onMenuItemClick(e, item, menuType)}
									style={isHovering ? hoverStyle : undefined}
									onMouseOver={() => onHoverItem(item.value ?? item.label)}
									onMouseLeave={() => onLeaveItem()}
									onFocus={() => onHoverItem(item.value ?? item.label)}
									onBlur={() => onLeaveItem()}
									role="menuitemradio"
									aria-checked={isSelected ? 'true' : 'false'}
								>
									<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
										{isSelected ? (
											<RadioButtonChecked className={classes.listItemIcon} htmlColor="#fff" />
										) : (
											<RadioButtonUnchecked className={classes.listItemIcon} htmlColor="#fff" />
										)}
									</ListItemIcon>
									<ListItemText classes={{ primary: classes.primaryListText }} primary={t(item.label)} />
								</ListItem>
							);
						})}

					{(menuType === 'subtitle' || menuType === 'language' || menuType === 'transcript') &&
						items.map((item: any, index: number) => {
							if (!item?.language?.code) {
								return null;
							}
							const isSelected =
								menuType === 'language'
									? selectedItem?.code === item.language?.code && selectedItem?.impaired === item.language?.impaired
									: selectedItem?.code === item.language?.code;
							const isHovering =
								menuType === 'language'
									? hoveringItem?.code === item.language?.code && hoveringItem?.impaired === item.language?.impaired
									: hoveringItem?.code === item.language?.code;

							return (
								<ListItem
									className={joinClassNames(classes.listItem, `${menuType}-item-${index}`)}
									onKeyDown={(e) => handleKeyDownOnOptions(e, item.language, menuType)}
									tabIndex={isSelected ? 0 : -1}
									key={`${item.language.code}-${index}-${menuKey}`}
									value={item.language.code}
									onClick={(e) => onMenuItemClick(e, item.language, menuType)}
									style={isHovering ? hoverStyle : undefined}
									onMouseOver={() => onHoverItem(item.language)}
									onMouseLeave={() => onLeaveItem()}
									onFocus={() => onHoverItem(item.language)}
									onBlur={() => onLeaveItem()}
									role="menuitemradio"
									aria-checked={isSelected ? 'true' : 'false'}
									lang={item.language?.code?.toLowerCase()}
								>
									<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
										{isSelected ? (
											<RadioButtonChecked className={classes.listItemIcon} htmlColor="#fff" />
										) : (
											<RadioButtonUnchecked className={classes.listItemIcon} htmlColor="#fff" />
										)}
									</ListItemIcon>
									<ListItemText classes={{ primary: classes.primaryListText }} primary={getItemPrimaryText(item)} />
								</ListItem>
							);
						})}

					{menuType === 'subtitle' && !isMediaLive && (
						<>
							<Divider aria-hidden="true" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '1px' }} />
							<ListItem
								tabIndex={0}
								className={classes.listItem}
								onClick={(e: any) => openSubtitleSettingMenu?.(e)}
								onKeyDown={(e: any) => openSubtitleSettingMenu?.(e)}
								style={hoveringItem?.code === 'subtitleSettings' ? hoverStyle : undefined}
								onMouseOver={() => onHoverItem({ code: 'subtitleSettings' })}
								onMouseLeave={() => onLeaveItem()}
								onFocus={() => onHoverItem({ code: 'subtitleSettings' })}
								onBlur={() => onLeaveItem()}
								aria-label={`${t('SETTINGS')}`}
								component='div'
								role='button'
							>
								<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
									<Settings className={classes.listItemIcon} htmlColor="#fff" aria-hidden="true" />
								</ListItemIcon>
								<ListItemText id={subHeaderId} classes={{ primary: classes.primaryListText }} primary={t('SETTINGS')} />
							</ListItem>
						</>
					)}
				</List>
			</>
		);
	};

	interface MenuSelectorProps {
		menuType: string;
		itemLabel: string;
		isMobile?: boolean;
		hoveringItem?: string;
		playerConfigs: any;
		onKeyDown: (_e: React.KeyboardEvent, _type?: string) => void;
		onCloseMenu: () => void;
		onHoverItem: (_menu: string) => void;
		onLeaveItem: () => void;
		onSelectMenu: (_menu: string) => void;
	}

	export const MenuSelector = ({
		menuType,
		isMobile,
		itemLabel,
		hoveringItem,
		playerConfigs,
		onSelectMenu,
		onKeyDown,
		onHoverItem,
		onLeaveItem,
	}: MenuSelectorProps) => {
		const color = useMemo(() => playerConfigs?.playerStyling?.primaryColor ?? '#126cfc', [playerConfigs?.playerStyling?.primaryColor]);
		const opacityN = useMemo(() => Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100), [playerConfigs?.playerStyling?.primaryOpacity]);
		const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacityN / 100 };
		const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
		const classes = useListCustomStyle();

		return (
			<ListItem
				className={joinClassNames(classes.listItem, hoveringItem === menuType ? 'menu-item--hovered' : '')}
				tabIndex={0}
				onClick={() => {
					onSelectMenu(menuType);
				}}
				onKeyDown={(e) => {
					onKeyDown(e, menuType);
				}}
				aria-haspopup="true"
				style={hoveringItem === menuType ? hoverStyle : undefined}
				onMouseOver={() => onHoverItem(menuType)}
				onMouseLeave={() => onLeaveItem()}
				onFocus={() => onHoverItem(menuType)}
				onBlur={() => onLeaveItem()}
				role="menuitem"
			>
				<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
					{getMenuIcon(menuType, classes.listItemIcon)}
				</ListItemIcon>
				<ListItemText classes={{ primary: classes.primaryListText }} primary={itemLabel} />
				<IconButton tabIndex={-1} aria-hidden={true} style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}>
					<ChevronRight
						className={joinClassNames(classes.listItemIcon, isSafari ? 'safari-fix' : '')}
						htmlColor="#fff"
						aria-hidden="true"
					/>
				</IconButton>
			</ListItem>
		);
	};
}
